import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { updateInventoryPancakeWarehouse } from "./pancakeWarehouseApi";

/**
 * Hàng đợi lưu "Kho Pancake" của danh mục kho.
 * - Gửi lần lượt: request trước xong mới gửi request sau, người dùng chọn nhanh cũng không dồn request lên server.
 * - Cùng một kho đổi nhiều lần khi lệnh chưa kịp gửi thì gộp lại, chỉ gửi giá trị cuối;
 *   chọn lại đúng giá trị cũ thì bỏ luôn lệnh.
 * - Hàng đợi nằm ở cấp module nên chuyển sang màn khác vẫn gửi tiếp. Lệnh chưa xong được lưu localStorage,
 *   đóng tab/F5 thì trình duyệt hỏi lại; nếu vẫn thoát, lần sau mở danh mục kho sẽ gửi tiếp.
 */

export type PancakeUpdateTask = {
  inventoryId: string;
  /** Mã kho, để báo lỗi cho người dùng */
  inventoryCode: string;
  pancakeWarehouseId: string | null;
  /** Giá trị trước khi đổi, dùng để bỏ lệnh khi người dùng chọn lại đúng giá trị này */
  previousPancakeWarehouseId: string | null;
};

/** saved: lệnh vừa lưu thành công (nếu có) để màn hình cập nhật lại dòng */
type Listener = (pending: PancakeUpdateTask[], saved?: PancakeUpdateTask) => void;

const STORAGE_KEY = "dm-inventory-pancake-update-queue";

// Khi isSending = true thì queue[0] là lệnh đang gửi, không được sửa/gộp vào nó
let queue: PancakeUpdateTask[] = [];
let isSending = false;
let failedCodes: string[] = [];
const listeners = new Set<Listener>();

const warnBeforeUnload = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  e.returnValue = "";
};

const setQueue = (next: PancakeUpdateTask[], saved?: PancakeUpdateTask) => {
  queue = next;
  try {
    if (next.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error(error);
  }
  listeners.forEach((listener) => listener(queue, saved));
};

const processQueue = async () => {
  if (isSending || !queue.length) return;
  isSending = true;
  window.addEventListener("beforeunload", warnBeforeUnload);

  while (queue.length) {
    const task = queue[0];
    let success = false;
    try {
      const res = await updateInventoryPancakeWarehouse(
        task.inventoryId,
        task.pancakeWarehouseId,
      );
      success = !!res?.success;
    } catch (error) {
      console.error(error);
    }
    if (!success) failedCodes.push(task.inventoryCode);
    setQueue(queue.slice(1), success ? task : undefined);
  }

  isSending = false;
  window.removeEventListener("beforeunload", warnBeforeUnload);
  // Báo lỗi gộp một lần khi hàng đợi đã trống: mỗi request của repository đều xoá hết thông báo cũ
  if (failedCodes.length) {
    NotificationExtension.Fails(
      `Cập nhật kho Pancake thất bại cho kho: ${failedCodes.join(", ")}`,
    );
    failedCodes = [];
  }
};

export const enqueuePancakeUpdate = (task: PancakeUpdateTask) => {
  const waitingIndex = queue.findIndex(
    (x, i) => x.inventoryId === task.inventoryId && !(isSending && i === 0),
  );
  if (waitingIndex < 0) {
    if (task.pancakeWarehouseId !== task.previousPancakeWarehouseId)
      setQueue([...queue, task]);
  } else {
    const merged = {
      ...task,
      previousPancakeWarehouseId: queue[waitingIndex].previousPancakeWarehouseId,
    };
    setQueue(
      merged.pancakeWarehouseId === merged.previousPancakeWarehouseId
        ? queue.filter((_, i) => i !== waitingIndex)
        : queue.map((x, i) => (i === waitingIndex ? merged : x)),
    );
  }
  processQueue();
};

/** Gửi tiếp các lệnh còn dở từ lần trước (đóng tab/F5 khi chưa gửi xong) */
export const resumePancakeUpdates = () => {
  if (!queue.length) {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
      if (Array.isArray(stored) && stored.length) setQueue(stored);
    } catch (error) {
      console.error(error);
    }
  }
  processQueue();
};

/** Nghe thay đổi hàng đợi, gọi ngay một lần với trạng thái hiện tại. Trả về hàm huỷ đăng ký. */
export const subscribePancakeUpdates = (listener: Listener) => {
  listeners.add(listener);
  listener(queue);
  return () => {
    listeners.delete(listener);
  };
};
